<template>
  <div class="chat-box">
    <!-- encabezado del chat -->
    <div v-if="selectedChat.name" class="chat-header">
      <span class="chat-title">{{ selectedChat.name }} </span>

      <!-- cerrar chat -->
      <LegoTooltip position="bottom">
        <span @click="closeChat">Cerrar conversación</span>
      </LegoTooltip>
    </div>

    <div class="chat-main">
      <!-- si el chat esta abierto -->
      <div v-if="selectedChat.name">
        <div class="chat-messages custom-scrollbar">
          <!-- loading -->
          <div v-if="loading && messages.length === 0" class="chat-loading">
            <div class="spinner"></div>
          </div>

          <!-- grupos de mensajes  -->
          <div
            v-for="(messages, dateGroup) in groupedMessages"
            :key="dateGroup"
            class="chat-date-group"
          >
            <!-- encabezado de fecha -->
            <div class="chat-date-header">{{ dateGroup }}</div>

            <!-- mensajes -->
            <div
              v-for="(item, index) in messages"
              :key="item.id"
              class="chat-msg"
              :class="{
                sent: item.senderId === userLoggedIn.id,
                received: item.senderId !== userLoggedIn.id,
                firstMsg: isFirstMessageOfGroup(messages, index),
              }"
            >
              <div
                class="msg-content"
                :class="{ 'msg-long': item.message.length > 30 }"
              >
                <p class="msg-text">{{ item.message }}</p>
                <div class="msg-info">
                  <small class="msg-time">
                    {{ formatMessageTime(item.createdAt) }}
                  </small>
                  <small
                    v-if="item.senderId === userLoggedIn.id"
                    class="msg-status"
                  >
                    <span v-if="!item.isRead" class="msg-received">
                      <!-- SVG check simple -->
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        width="15"
                        height="15"
                        stroke-width="2"
                      >
                        <path d="M5 12l5 5l10 -10"></path>
                      </svg>
                    </span>
                    <span v-else class="msg-accessed">
                      <!-- SVG doble check -->
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        width="15"
                        height="15"
                        stroke-width="2"
                      >
                        <path d="M7 12l5 5l10 -10"></path>
                        <path d="M2 12l5 5m5 -5l5 -5"></path>
                      </svg>
                    </span>
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- chat sin mensajes -->
          <div
            v-if="messages.length === 0 && !loading"
            class="not-conversation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="100"
              height="100"
              stroke-width="1"
            >
              <path d="M12 11v.01"></path>
              <path d="M8 11v.01"></path>
              <path d="M16 11v.01"></path>
              <path
                d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3z"
              ></path>
            </svg>
            <span>Escribe un mensaje</span>
          </div>
        </div>

        <!-- input de mensaje -->
        <div class="chat-input">
          <form class="w-100" @submit.prevent="sendMessage">
            <div class="input-container" :class="{ isTyping: newMessage }">
              <textarea
                v-model="newMessage"
                placeholder="Mensaje"
                @keyup.enter="sendMessage"
                rows="1"
                required
              ></textarea>
              <button
                v-if="!sendingMessage"
                :disabled="!newMessage"
                type="submit"
              >
                <i class="fas fa-arrow-up"></i>
              </button>
              <div v-else class="spinner sending"></div>
            </div>
          </form>
        </div>
      </div>

      <!-- si no hay chat seleccionado -->
      <div v-else class="not-conversation">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          width="100"
          height="100"
          stroke-width="1"
        >
          <path
            d="M21 14l-3 -3h-7a1 1 0 0 1 -1 -1v-6a1 1 0 0 1 1 -1h9a1 1 0 0 1 1 1v10"
          ></path>
          <path d="M14 15v2a1 1 0 0 1 -1 1h-7l-3 3v-10a1 1 0 0 1 1 -1h2"></path>
        </svg>
        <span>Inicia una conversación</span>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import LegoTooltip from "@/components/Lego/LegoTooltip.vue";

export default {
  components: {
    LegoTooltip,
  },
  props: {
    selectedChat: {
      type: Object,
      required: true,
      default: () => ({
        legoCardId: null,
        receiverId: null,
        senderId: null,
        isRead: false,
        name: null,
      }),
    },
    messages: {
      type: Array,
      required: true,
      default: () => [
        {
          id: "",
          legoCardId: "",
          senderId: "",
          receiverId: "",
          message: "",
          createdAt: "",
          isRead: false,
        },
      ],
    },
    loading: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      newMessage: "",
      sendingMessage: false,
    };
  },
  watch: {
    messages: {
      immediate: true,
      // eslint-disable-next-line no-unused-vars
      handler(newMessages, oldMessages) {
        console.log("Nuevos mensajes recibidos en LegoChatBox:", newMessages);

        if (Array.isArray(newMessages) && newMessages.length > 0) {
          const chatMessages = this.$el.querySelector(".chat-messages");

          const isAtBottom =
            chatMessages.scrollHeight - chatMessages.scrollTop <=
            chatMessages.clientHeight + 50;

          this.scrollToBottom(isAtBottom);
        }

        this.$nextTick(() => {
          const chatMessages = this.$el.querySelector(".chat-messages");

          if (chatMessages) {
            chatMessages.addEventListener("scroll", this.handleScroll);
          }
        });
      },
    },
  },

  beforeDestroy() {
    const chatMessages = this.$el.querySelector(".chat-messages");
    if (chatMessages) {
      chatMessages.removeEventListener("scroll", this.handleScroll);
    }
  },
  computed: {
    userLoggedIn() {
      return this.$store.getters.userLoggedIn;
    },
    groupedMessages() {
      const grouped = {};
      this.messages?.forEach((msg) => {
        const dateKey = this.getDateGroup(msg.createdAt);
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(msg);
      });
      return grouped;
    },
  },
  methods: {
    handleScroll() {
      const chatMessages = this.$el.querySelector(".chat-messages");
      if (chatMessages && chatMessages.scrollTop === 0) {
        console.log("Scroll arriba - Cargando más mensajes...");
        this.$emit("loadMoreMessages");
      }
    },
    isFirstMessageOfGroup(messages, index) {
      if (index === 0) return true;
      return messages[index].senderId !== messages[index - 1].senderId;
    },
    closeChat() {
      this.$emit("closeChat");
      this.newMessage = "";
    },
    async sendMessage() {
      if (this.newMessage.trim() !== "") {
        this.sendingMessage = true;
        try {
          let data = {
            ...this.selectedChat,
            message: this.newMessage,
            createdAt: this.$moment
              .tz(new Date(), "America/Bogota")
              .format("YYYY-MM-DDTHH:mm:ss"),
          };
          const res = await this.$store.dispatch("hl_post", {
            path: "Lego/chat/send",
            data,
          });

          console.log(res);
          if (res.messageId) {
            this.$emit("sendedMessage", {
              ...data,
              id: res.messageId,
            });

            const recipientId =
              data.senderId === this.userLoggedIn.id
                ? data.receiverId
                : data.senderId;

            this.$emit("updateMessagesInParent", {
              userId: recipientId,
              unreadCount: 0,
              lastMessageDate: data.createdAt,
              lastMessage: data.message,
              lastMessageSentByMe: true,
              lastMessageReadByRecipient: false,
            });
          }
          this.scrollToBottom(true);
        } catch (error) {
          console.error("Error al guardar cambios:", error);
          Swal.fire("Error!", "No fue posible enviar el mensaje.", "error");
        } finally {
          this.newMessage = "";
          this.sendingMessage = false;
        }
      }
    },
    scrollToBottom(force = false) {
      this.$nextTick(() => {
        const chatMessages = this.$el.querySelector(".chat-messages");
        if (!chatMessages) return;

        const isNearBottom =
          chatMessages.scrollHeight - chatMessages.scrollTop <=
          chatMessages.clientHeight + 50;

        if (force || isNearBottom) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      });
    },
    getDateGroup(timestamp) {
      const messageDate = new Date(timestamp);
      const now = new Date();

      const messageDay = messageDate.toDateString();
      const today = now.toDateString();

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayDay = yesterday.toDateString();

      if (messageDay === today) {
        return "Hoy";
      } else if (messageDay === yesterdayDay) {
        return "Ayer";
      } else if (now - messageDate < 7 * 24 * 60 * 60 * 1000) {
        return messageDate.toLocaleDateString("es-ES", { weekday: "long" });
      } else {
        return messageDate.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }
    },
    formatMessageTime(timestamp) {
      const messageDate = new Date(timestamp);
      return messageDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.chat-date-group {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.chat-date-header {
  text-align: center;
  font-size: 0.6rem;
  padding: 5px 10px;
  width: fit-content;
  margin-inline: auto;
  border-radius: 10px;
  position: sticky;
  top: 0.1rem;
  z-index: 2;
  background-color: #e1e1e1;
  .dark & {
    background-color: #151618;
  }
}
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-title {
  font-size: 1.1rem;
  color: black;
  .dark & {
    color: white;
  }
}

.chat-main {
  height: 480px;
  min-height: 480px;
}

.chat-input form {
  display: flex;
}

.chat-messages {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 420px;
  min-height: 420px;
  padding: 0 1rem 1rem 1rem;
  border-radius: 5px;
  scroll-behavior: smooth;
  background-color: rgba(129, 129, 129, 0.05);
  .dark & {
    background-color: rgba(21, 22, 24, 0.3);
  }
}

.chat-msg {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  max-width: 70%;
}

/* Mensajes enviados */
.chat-msg.sent {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.chat-msg.sent .msg-content {
  background-color: #7678ee;
  border-radius: 6px;
  & .msg-text {
    color: white;
  }
}

.chat-msg.firstMsg .msg-content {
  margin-top: 1rem;
}

.chat-msg.sent.firstMsg .msg-content {
  border-radius: 6px 0 6px 6px;
}

/* Mensajes recibidos */
.chat-msg.received {
  align-self: flex-start;
}

.chat-msg.received .msg-content {
  border-radius: 6px;
  background-color: var(--lego-shadow-hover);
  & .msg-time,
  .msg-text {
    color: rgba(0, 0, 0, 0.7);
  }

  .dark & .msg-time,
  .dark & .msg-text {
    color: rgba(255, 255, 255, 0.7);
  }
}
.chat-msg.received.firstMsg .msg-content {
  border-radius: 0 6px 6px 6px;
}

.chat-msg.sent.firstMsg .msg-content::after {
  content: "";
  position: absolute;
  top: 0;
  right: -10px;
  width: 0;
  height: 0;
  border-right: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 10px solid transparent;
  border-top: 10px solid #7678ee;
}

.chat-msg.received.firstMsg .msg-content::after {
  content: "";
  position: absolute;
  top: 0;
  left: -10px;
  width: 10.07px;
  height: 1rem;
  border-right: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 10px solid transparent;
  border-top: 10px solid #e1e1e1;
}

.dark .chat-msg.received .msg-content::after {
  border-top: 10px solid #131416;
}

/* Contenedor del mensaje */
.msg-content {
  position: relative;
  padding: 8px 12px;
  max-width: 100%;
  font-size: 0.8rem;
  word-wrap: break-word;
  line-height: 1.4;
  display: flex;
  gap: 0.5rem;
}

.msg-content.msg-long {
  flex-direction: column;
  gap: 2px;
}

/* Texto del mensaje */
.msg-text {
  margin: 0;
}

.msg-info {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.3rem;
}

/* Hora del mensaje */
.msg-time,
.msg-received,
.msg-accessed {
  align-self: flex-end;
  font-size: 9px;
  color: rgba(255, 255, 255, 0.7);
}

/* Avatar */
.avatar {
  margin: 0 8px;
}

/* not conversation */
.not-conversation {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  height: 400px;

  color: #7678ee;
  & span {
    color: #7678ee;
  }

  & svg {
    opacity: 0.3;
  }
}

.chat-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

/* chat input */
.chat-input {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 0.3rem;
}

.input-container {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  margin-inline: auto;
  border: 1px solid #ccc;
  border-radius: 20px;
  padding: 5px;
  .dark & {
    border-color: #555;
  }
}
.input-container textarea {
  flex-grow: 1;
  border: none;
  outline: none;
  resize: none;
  padding: 5px 0.5rem;
  font-size: 0.8rem;
  overflow-y: hidden;
  background-color: transparent;
  .dark & {
    color: white;
  }
}
.input-container button {
  border: none;
  background: none;
  width: 2rem;
  height: 2rem;
  color: #555;
  font-size: 1rem;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.input-container button:disabled {
  cursor: default;
}

.input-container.isTyping button {
  background-color: #7678ee;
  color: white;
}

.spinner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: radial-gradient(farthest-side, #7678ee 94%, #0000) top/6.4px 6.4px
      no-repeat,
    conic-gradient(#0000 30%, #7678ee);
  -webkit-mask: radial-gradient(
    farthest-side,
    #0000 calc(100% - 6.4px),
    #000 0
  );
  mask: radial-gradient(farthest-side, #0000 calc(100% - 6.4px), #000 0);
  animation: spinner-c7wet2 0.6s infinite linear;
}

.spinner.sending {
  width: 20px;
  height: 20px;
}

@keyframes spinner-c7wet2 {
  100% {
    transform: rotate(1turn);
  }
}
</style>
